import React, { useState } from 'react';
import { Operator, OperatorId, PackageItem, AirtimeDenomination } from '../../types';
import { useAdmin } from '../../context/AdminContext';
import { OperatorLogo } from '../../components/common/BrandLogo';
import {
  ShoppingBag,
  Sliders,
  PlusCircle,
  Settings,
  CheckCircle2,
  XCircle,
  RefreshCw,
  PhoneCall,
  X,
} from 'lucide-react';

export const AdminPackagesPage: React.FC = () => {
  const {
    operators,
    packages,
    airtimeDenominations,
    loading,
    actionLoading,
    refresh,
    togglePackageStock,
    updatePackagePrice,
    updatePackage,
    createPackage,
    updateOperatorStatus,
    saveAirtimeDenomination,
    toggleAirtimeDenomination,
    deleteAirtimeDenomination,
  } = useAdmin();

  // Stock management filter
  const [selectedStockOp, setSelectedStockOp] = useState<OperatorId | 'all'>('all');

  // Edit package dialog (stock, price, name, data)
  const [editingPackage, setEditingPackage] = useState<PackageItem | null>(null);
  const [editStockActive, setEditStockActive] = useState<number>(1);
  const [editSellingPrice, setEditSellingPrice] = useState<string>('');
  const [editCostPrice, setEditCostPrice] = useState<string>('');
  const [editPkgName, setEditPkgName] = useState('');
  const [editPkgData, setEditPkgData] = useState('');

  // Add package dialog
  const [showAddPackage, setShowAddPackage] = useState(false);
  const [newPkgOp, setNewPkgOp] = useState<OperatorId>('mpt');
  const [newPkgName, setNewPkgName] = useState('');
  const [newPkgData, setNewPkgData] = useState('');
  const [newPkgValidity, setNewPkgValidity] = useState('30');
  const [newPkgSellingPrice, setNewPkgSellingPrice] = useState('');
  const [newPkgCostPrice, setNewPkgCostPrice] = useState('');

  // Main view tab: 'packages' | 'airtime'
  const [activeMainTab, setActiveMainTab] = useState<'packages' | 'airtime'>('packages');
  const [selectedAirtimeOp, setSelectedAirtimeOp] = useState<OperatorId | 'all'>('all');

  // Add / Edit denomination state
  const [showAddDenomination, setShowAddDenomination] = useState(false);
  const [editingDenomination, setEditingDenomination] = useState<AirtimeDenomination | null>(null);
  const [denomOp, setDenomOp] = useState<OperatorId>('mpt');
  const [denomAmount, setDenomAmount] = useState('');
  const [denomSellingPrice, setDenomSellingPrice] = useState('');
  const [denomCostPrice, setDenomCostPrice] = useState('');

  const handleOpenAddDenom = (op?: OperatorId) => {
    setEditingDenomination(null);
    setDenomOp(op || (selectedAirtimeOp !== 'all' ? selectedAirtimeOp : 'mpt'));
    setDenomAmount('');
    setDenomSellingPrice('');
    setDenomCostPrice('');
    setShowAddDenomination(true);
  };

  const handleOpenEditDenom = (den: AirtimeDenomination) => {
    setEditingDenomination(den);
    setDenomOp(den.operator_id);
    setDenomAmount(den.airtime_amount.toString());
    setDenomSellingPrice(den.selling_price.toString());
    setDenomCostPrice(den.cost_price ? den.cost_price.toString() : '');
    setShowAddDenomination(true);
  };

  const handleSaveDenomination = async () => {
    const amt = parseInt(denomAmount.replace(/\D/g, ''), 10);
    const sPrice = parseInt(denomSellingPrice.replace(/\D/g, ''), 10);
    const cPrice = parseInt(denomCostPrice.replace(/\D/g, '') || '0', 10);

    if (!amt || amt <= 0) return alert('ဘေလ်ပမာဏ မှန်ကန်စွာ ဖြည့်ပေးပါ');
    if (!sPrice || sPrice <= 0) return alert('ရောင်းစျေး မှန်ကန်စွာ ဖြည့်ပေးပါ');

    try {
      await saveAirtimeDenomination({
        id: editingDenomination ? editingDenomination.id : undefined,
        operator_id: denomOp,
        airtime_amount: amt,
        selling_price: sPrice,
        cost_price: isNaN(cPrice) ? 0 : cPrice,
        is_active: editingDenomination ? editingDenomination.is_active : 1,
      });
      setShowAddDenomination(false);
      setEditingDenomination(null);
    } catch (err: any) {
      alert(err.message || 'ဘေလ်ပမာဏ သိမ်းဆည်းမှု မအောင်မြင်ပါ');
    }
  };

  const handleDeleteDenom = async (id: string) => {
    if (!confirm('ဤ ဘေလ်ပမာဏကို ဖျက်ရန် သေချာပါသလား?')) return;
    try {
      await deleteAirtimeDenomination(id);
    } catch (err: any) {
      alert(err.message || 'ဖျက်၍ မရပါ');
    }
  };

  // Operator status edit dialog
  const [editingOperator, setEditingOperator] = useState<Operator | null>(null);
  const [newStatus, setNewStatus] = useState<'open' | 'closed_daily_limit' | 'closed_other'>('open');
  const [statusReason, setStatusReason] = useState('');

  const handleUpdateOperator = async () => {
    if (!editingOperator) return;
    try {
      await updateOperatorStatus(editingOperator.id, newStatus, statusReason);
      setEditingOperator(null);
    } catch (err: any) {
      alert(err.message || 'Operator status update failed');
    }
  };

  const handleOpenEdit = (pkg: PackageItem) => {
    setEditingPackage(pkg);
    setEditStockActive(pkg.is_active);
    setEditSellingPrice(pkg.selling_price.toString());
    setEditCostPrice(pkg.cost_price.toString());
    setEditPkgName(pkg.name);
    setEditPkgData(pkg.data_allowance);
  };

  const handleSavePackage = async () => {
    if (!editingPackage) return;
    const sPrice = parseInt(editSellingPrice.replace(/\D/g, ''), 10);
    const cPrice = parseInt(editCostPrice.replace(/\D/g, ''), 10);
    if (!sPrice) return alert('ကျေးဇူးပြု၍ ရောင်းစျေး ဖြည့်ပေးပါ');

    try {
      await updatePackage(editingPackage.id, {
        is_active: editStockActive,
        selling_price: sPrice,
        cost_price: isNaN(cPrice) ? undefined : cPrice,
        name: editPkgName.trim() || editingPackage.name,
        data_allowance: editPkgData.trim() || editingPackage.data_allowance,
      });
      setEditingPackage(null);
    } catch (err: any) {
      alert(err.message || 'ပြင်ဆင်မှု မအောင်မြင်ပါ');
    }
  };

  const handleCreateNewPackage = async () => {
    if (!newPkgName || !newPkgSellingPrice) {
      alert('Package အမည်နှင့် ရောင်းစျေး ဖြည့်ပေးပါ');
      return;
    }
    try {
      await createPackage({
        operator_id: newPkgOp,
        name: newPkgName,
        data_allowance: newPkgData || '1 GB',
        validity_days: parseInt(newPkgValidity, 10) || 30,
        selling_price: parseInt(newPkgSellingPrice.replace(/\D/g, ''), 10),
        cost_price: parseInt(newPkgCostPrice.replace(/\D/g, '') || '0', 10),
      });
      setShowAddPackage(false);
      setNewPkgName('');
      setNewPkgData('');
      setNewPkgSellingPrice('');
      setNewPkgCostPrice('');
    } catch (err: any) {
      alert(err.message || 'Package creation failed');
    }
  };

  return (
    <div className="space-y-6 pb-28">
      {/* Title & Quick Actions */}
      <div className="px-1">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-base sm:text-lg font-bold text-plum-dark flex items-center space-x-2 min-w-0">
            {activeMainTab === 'packages' ? (
              <>
                <ShoppingBag className="w-5 h-5 text-primary shrink-0" />
                <span className="truncate">Package စာရင်း</span>
              </>
            ) : (
              <>
                <PhoneCall className="w-5 h-5 text-primary shrink-0" />
                <span className="truncate">ဖုန်းဘေလ် သတ်မှတ်ဈေးနှုန်းများ</span>
              </>
            )}
          </h2>

          <div className="flex items-center space-x-1.5 shrink-0">
            <button
              onClick={refresh}
              title="အသစ်ပြန်တင်မည်"
              disabled={loading}
              className="p-1.5 sm:p-2 rounded-xl bg-surface-card border border-plum-border text-plum-dark hover:text-primary transition shrink-0"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-primary' : ''}`} />
            </button>
            {activeMainTab === 'packages' ? (
              <button
                onClick={() => setShowAddPackage(true)}
                className="bg-primary hover:bg-primary-hover active:scale-[0.98] text-white font-semibold text-xs py-1.5 sm:py-2 px-2.5 sm:px-3 rounded-xl transition shadow-xs flex items-center space-x-1 shrink-0"
              >
                <PlusCircle className="w-3.5 h-3.5 text-white" />
                <span>Package အသစ်ထည့်မည်</span>
              </button>
            ) : (
              <button
                onClick={() => handleOpenAddDenom()}
                className="bg-primary hover:bg-primary-hover active:scale-[0.98] text-white font-semibold text-xs py-1.5 sm:py-2 px-2.5 sm:px-3 rounded-xl transition shadow-xs flex items-center space-x-1 shrink-0"
              >
                <PlusCircle className="w-3.5 h-3.5 text-white" />
                <span>ဘေလ်ပမာဏ အသစ်ထည့်မည်</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Tab Switcher */}
      <div className="flex bg-surface-card p-1 rounded-2xl border border-plum-border">
        <button
          onClick={() => setActiveMainTab('packages')}
          className={`flex-1 py-2.5 rounded-xl text-xs font-bold transition flex items-center justify-center space-x-1.5 ${
            activeMainTab === 'packages'
              ? 'bg-primary text-white shadow-xs'
              : 'text-plum-muted hover:text-plum-dark'
          }`}
        >
          <ShoppingBag className="w-4 h-4" />
          <span>Data Packages ({packages.length})</span>
        </button>
        <button
          onClick={() => setActiveMainTab('airtime')}
          className={`flex-1 py-2.5 rounded-xl text-xs font-bold transition flex items-center justify-center space-x-1.5 ${
            activeMainTab === 'airtime'
              ? 'bg-primary text-white shadow-xs'
              : 'text-plum-muted hover:text-plum-dark'
          }`}
        >
          <PhoneCall className="w-4 h-4" />
          <span>ဖုန်းဘေလ် သတ်မှတ်ဈေးနှုန်းများ ({airtimeDenominations.length})</span>
        </button>
      </div>

      {activeMainTab === 'packages' ? (
        <>
          {/* OPERATOR AVAILABILITY CONTROLS */}
          <div className="space-y-3">
            <div className="flex items-center justify-between px-1">
              <h3 className="text-xs font-bold text-plum-muted uppercase tracking-wider flex items-center space-x-1.5">
                <Sliders className="w-3.5 h-3.5" />
                <span>အော်ပရေတာ တစ်ရက်တာ ကန့်သတ်ချက်များ</span>
              </h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {operators.map((op) => (
                <div
                  key={op.id}
                  className="bg-surface-card rounded-2xl border border-plum-border p-4 shadow-xs space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2.5">
                      <OperatorLogo id={op.id} size="md" className="rounded-xl shadow-xs" />
                      <div>
                        <h4 className="font-bold text-plum-dark text-base uppercase">{op.name}</h4>
                        <p className="text-[11px] text-plum-muted">
                          ဆိုင်းငံ့ Order: <b>{op.pendingOrdersCount || 0}</b> ခု
                        </p>
                      </div>
                    </div>

                    <span
                      className={`text-[10px] font-bold px-2.5 py-1 rounded-full border ${
                        op.package_status === 'open'
                          ? 'bg-green-500/10 text-status-green border-green-500/30'
                          : op.package_status === 'closed_daily_limit'
                          ? 'bg-amber-500/10 text-status-amber border-amber-500/30'
                          : 'bg-red-500/10 text-status-red border-red-500/30'
                      }`}
                    >
                      {op.package_status === 'open'
                        ? '🟢 ဖွင့်ထားပါသည်'
                        : op.package_status === 'closed_daily_limit'
                        ? '🟡 Limit ပြည့်၍ပိတ်'
                        : '🔴 ယာယီပိတ်ထား'}
                    </span>
                  </div>

                  {op.status_reason && (
                    <p className="text-[11px] text-plum-muted bg-surface-page p-2 rounded-lg border border-plum-border/40">
                      အကြောင်းရင်း: {op.status_reason}
                    </p>
                  )}

                  <button
                    type="button"
                    onClick={() => {
                      setEditingOperator(op);
                      setNewStatus(op.package_status);
                      setStatusReason(op.status_reason || '');
                    }}
                    className="w-full bg-surface-page hover:bg-surface-accent border border-plum-border text-primary font-semibold text-xs py-2 rounded-xl transition touch-target"
                  >
                    အခြေအနေ ပြောင်းလဲမည် (Open / Close)
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* STOCK & PACKAGE CATALOG */}
          <div className="space-y-4">
            {/* Operator Filter Pills */}
            <div className="flex space-x-1.5 overflow-x-auto pb-1 scrollbar-none">
              {[
                { id: 'all', label: 'အားလုံး' },
                { id: 'mpt', label: 'MPT' },
                { id: 'atom', label: 'ATOM' },
                { id: 'u9', label: 'U9' },
                { id: 'mytel', label: 'MYTEL' },
              ].map((op) => (
                <button
                  key={op.id}
                  onClick={() => setSelectedStockOp(op.id as any)}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition ${
                    selectedStockOp === op.id
                      ? 'bg-primary text-white shadow-xs'
                      : 'bg-surface-card border border-plum-border text-plum-muted hover:text-plum-dark'
                  }`}
                >
                  {op.label}{' '}
                  <span className="opacity-70 text-[10px]">
                    (
                    {op.id === 'all'
                      ? packages.length
                      : packages.filter((p) => p.operator_id === op.id).length}
                    )
                  </span>
                </button>
              ))}
            </div>

            {/* Packages List */}
            <div className="space-y-3">
              {packages
                .filter((p) => selectedStockOp === 'all' || p.operator_id === selectedStockOp)
                .map((pkg) => {
                  const isInStock = pkg.is_active === 1;
                  const margin = pkg.selling_price - pkg.cost_price;

                  return (
                    <div
                      key={pkg.id}
                      className={`bg-surface-card rounded-2xl border p-4 shadow-xs space-y-3 transition ${
                        isInStock ? 'border-plum-border' : 'border-red-500/30 bg-red-950/10'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center space-x-2">
                            <span className="text-[10px] font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-md uppercase font-mono">
                              {pkg.operator_id}
                            </span>
                            <span
                              className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                                isInStock
                                  ? 'bg-green-500/10 text-status-green border-green-500/30'
                                  : 'bg-red-500/10 text-status-red border-red-500/30'
                              }`}
                            >
                              {isInStock ? '🟢 စတော့ရှိ' : '🔴 စတော့ကုန်'}
                            </span>
                          </div>
                          <h4 className="font-bold text-plum-dark text-sm mt-1.5">{pkg.name}</h4>
                          <p className="text-[11px] text-plum-muted mt-0.5">
                            {pkg.data_allowance} • သက်တမ်း {pkg.validity_days} ရက်
                            {pkg.eligibility ? ` • ${pkg.eligibility}` : ''}
                          </p>
                        </div>

                        <div className="text-right">
                          <span className="text-base font-bold text-primary">
                            {pkg.selling_price.toLocaleString()} Ks
                          </span>
                          <p className="text-[10px] text-plum-muted mt-0.5">
                            ရင်းစျေး: {pkg.cost_price.toLocaleString()} Ks (
                            <span className="text-status-green font-semibold">+{margin.toLocaleString()}</span>)
                          </p>
                        </div>
                      </div>

                      {/* Stock Actions */}
                      <div className="pt-2 border-t border-plum-border/60 flex items-center space-x-2">
                        <button
                          onClick={() => togglePackageStock(pkg.id)}
                          className={`flex-1 py-2 px-3 rounded-xl text-xs font-bold border transition touch-target flex items-center justify-center space-x-1.5 ${
                            isInStock
                              ? 'bg-surface-page hover:bg-red-500/10 text-plum-dark hover:text-status-red border-plum-border hover:border-red-500/30'
                              : 'bg-green-500/10 hover:bg-green-500/20 text-status-green border-green-500/30'
                          }`}
                        >
                          {isInStock ? (
                            <>
                              <XCircle className="w-3.5 h-3.5" />
                              <span>စတော့ပိတ်မည်</span>
                            </>
                          ) : (
                            <>
                              <CheckCircle2 className="w-3.5 h-3.5" />
                              <span>စတော့ဖွင့်မည်</span>
                            </>
                          )}
                        </button>

                        <button
                          onClick={() => handleOpenEdit(pkg)}
                          className="py-2 px-3.5 rounded-xl bg-surface-page hover:bg-surface-accent border border-plum-border text-primary text-xs font-bold transition flex items-center space-x-1 touch-target"
                        >
                          <Settings className="w-3.5 h-3.5" />
                          <span>ပြင်ဆင်မည်</span>
                        </button>
                      </div>
                    </div>
                  );
                })}

              {packages.filter((p) => selectedStockOp === 'all' || p.operator_id === selectedStockOp).length === 0 && (
                <div className="p-8 text-center bg-surface-card rounded-2xl border border-plum-border text-plum-muted text-xs">
                  Package မရှိသေးပါ။ "+ Package အသစ်ထည့်မည်" ဖြင့် ထည့်သွင်းနိုင်ပါသည်။
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        /* AIRTIME DENOMINATIONS VIEW */
        <div className="space-y-4">
          {/* Operator Filter Pills */}
          <div className="flex space-x-1.5 overflow-x-auto pb-1 scrollbar-none">
            {[
              { id: 'all', label: 'အားလုံး' },
              { id: 'mpt', label: 'MPT' },
              { id: 'atom', label: 'ATOM' },
              { id: 'u9', label: 'U9' },
              { id: 'mytel', label: 'MYTEL' },
            ].map((op) => (
              <button
                key={op.id}
                onClick={() => setSelectedAirtimeOp(op.id as any)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition ${
                  selectedAirtimeOp === op.id
                    ? 'bg-primary text-white shadow-xs'
                    : 'bg-surface-card border border-plum-border text-plum-muted hover:text-plum-dark'
                }`}
              >
                {op.label}{' '}
                <span className="opacity-70 text-[10px]">
                  (
                  {op.id === 'all'
                    ? airtimeDenominations.length
                    : airtimeDenominations.filter((d) => d.operator_id === op.id).length}
                  )
                </span>
              </button>
            ))}
          </div>

          {/* Info note */}
          <div className="bg-primary/5 border border-primary/20 rounded-2xl p-3.5 text-xs text-plum-muted space-y-1">
            <p className="font-semibold text-plum-dark flex items-center space-x-1.5">
              <Sliders className="w-4 h-4 text-primary" />
              <span>ဖုန်းဘေလ် ရောင်းချမှု စည်းမျဉ်း</span>
            </p>
            <p>
              Customer များသည် ဤနေရာတွင် သတ်မှတ်ထားသော ပမာဏများကိုသာ ရွေးချယ်ဝယ်ယူနိုင်ပါမည်။ 
              သတ်မှတ်ထားသော ရောင်းစျေး (Fixed MMK) အတိုင်းသာ ပေးချေရမည်ဖြစ်ပြီး လွှဲခ မပါဝင်ဘဲ Transfer Fee ပါ အပြီးအစီးဖြစ်ပါသည်။
            </p>
          </div>

          {/* Denominations List */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {airtimeDenominations
              .filter((d) => selectedAirtimeOp === 'all' || d.operator_id === selectedAirtimeOp)
              .map((den) => {
                const isActive = den.is_active === 1;
                const margin = den.selling_price - (den.cost_price || 0);

                return (
                  <div
                    key={den.id}
                    className={`bg-surface-card rounded-2xl border p-4 shadow-xs space-y-3 transition ${
                      isActive ? 'border-plum-border' : 'border-red-500/30 bg-red-950/10 opacity-75'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2">
                          <span className="text-[10px] font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-md uppercase font-mono">
                            {den.operator_id}
                          </span>
                          <span
                            className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                              isActive
                                ? 'bg-green-500/10 text-status-green border-green-500/30'
                                : 'bg-red-500/10 text-status-red border-red-500/30'
                            }`}
                          >
                            {isActive ? '🟢 ရောင်းချနေသည်' : '🔴 ရပ်နားထားသည်'}
                          </span>
                        </div>
                        <h4 className="font-extrabold text-plum-dark text-base">
                          {den.airtime_amount.toLocaleString()} Ks ဘေလ်
                        </h4>
                      </div>

                      <div className="text-right">
                        <span className="text-base font-bold text-primary">
                          {den.selling_price.toLocaleString()} Ks
                        </span>
                        <p className="text-[10px] text-plum-muted mt-0.5">
                          ရင်း: {(den.cost_price || 0).toLocaleString()} Ks (
                          <span className={margin >= 0 ? 'text-status-green font-semibold' : 'text-status-red font-semibold'}>
                            {margin >= 0 ? `+${margin.toLocaleString()}` : margin.toLocaleString()}
                          </span>)
                        </p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="pt-2 border-t border-plum-border/60 flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={() => toggleAirtimeDenomination(den.id)}
                        className={`flex-1 py-2 px-3 rounded-xl text-xs font-bold border transition touch-target flex items-center justify-center space-x-1.5 ${
                          isActive
                            ? 'bg-surface-page hover:bg-red-500/10 text-plum-dark hover:text-status-red border-plum-border hover:border-red-500/30'
                            : 'bg-green-500/10 hover:bg-green-500/20 text-status-green border-green-500/30'
                        }`}
                      >
                        {isActive ? (
                          <>
                            <XCircle className="w-3.5 h-3.5" />
                            <span>ရောင်းချမှု ပိတ်မည်</span>
                          </>
                        ) : (
                          <>
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            <span>ပြန်ဖွင့်မည်</span>
                          </>
                        )}
                      </button>

                      <button
                        type="button"
                        onClick={() => handleOpenEditDenom(den)}
                        className="p-2 rounded-xl bg-surface-page hover:bg-surface-accent border border-plum-border text-primary text-xs font-bold transition flex items-center justify-center touch-target"
                        title="ပြင်ဆင်မည်"
                      >
                        <Settings className="w-3.5 h-3.5" />
                      </button>

                      <button
                        type="button"
                        onClick={() => handleDeleteDenom(den.id)}
                        className="p-2 rounded-xl bg-surface-page hover:bg-red-500/10 border border-plum-border text-plum-muted hover:text-status-red text-xs font-bold transition flex items-center justify-center touch-target"
                        title="ဖျက်မည်"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
          </div>

          {airtimeDenominations.filter((d) => selectedAirtimeOp === 'all' || d.operator_id === selectedAirtimeOp).length === 0 && (
            <div className="p-8 text-center bg-surface-card rounded-2xl border border-plum-border text-plum-muted text-xs">
              သတ်မှတ်ထားသော ဘေလ်ပမာဏ မရှိသေးပါ။ "+ ဘေလ်ပမာဏ အသစ်ထည့်မည်" ဖြင့် ထည့်သွင်းနိုင်ပါသည်။
            </div>
          )}
        </div>
      )}

      {/* Operator Status Edit Modal */}
      {editingOperator && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-xs p-4">
          <div className="bg-surface-card w-full max-w-sm rounded-3xl border border-plum-border p-5 shadow-2xl space-y-4">
            <h3 className="font-bold text-plum-dark text-base uppercase">
              {editingOperator.name} Availability ပြင်ဆင်ရန်
            </h3>

            <div className="space-y-2">
              <label className="block text-xs font-semibold text-plum-dark">အခြေအနေ ရွေးပါ</label>
              <div className="space-y-1.5">
                {[
                  { id: 'open', label: '🟢 ဖွင့်ထားမည် (Open)' },
                  { id: 'closed_daily_limit', label: '🟡 Daily Limit ပြည့်၍ ပိတ်မည်' },
                  { id: 'closed_other', label: '🔴 အခြားအကြောင်းကြောင့် ပိတ်မည်' },
                ].map((st) => (
                  <button
                    key={st.id}
                    type="button"
                    onClick={() => setNewStatus(st.id as any)}
                    className={`w-full p-2.5 rounded-xl border text-xs font-bold text-left transition ${
                      newStatus === st.id
                        ? 'bg-primary/10 border-primary text-primary'
                        : 'bg-surface-page border-plum-border text-plum-muted'
                    }`}
                  >
                    {st.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-plum-dark mb-1">
                အကြောင်းရင်း ဖော်ပြချက်
              </label>
              <input
                type="text"
                value={statusReason}
                onChange={(e) => setStatusReason(e.target.value)}
                placeholder="ဥပမာ: Today transfer limit reached"
                className="w-full bg-surface-page border border-plum-border rounded-xl px-3 py-2 text-xs text-plum-dark"
              />
            </div>

            <div className="flex space-x-2 pt-2">
              <button
                type="button"
                onClick={() => setEditingOperator(null)}
                className="flex-1 border border-plum-border py-2.5 rounded-xl text-xs font-semibold text-plum-muted hover:text-plum-dark"
              >
                ပယ်ဖျက်မည်
              </button>
              <button
                type="button"
                disabled={actionLoading}
                onClick={handleUpdateOperator}
                className="flex-1 bg-primary text-white py-2.5 rounded-xl text-xs font-semibold hover:bg-primary-hover transition"
              >
                {actionLoading ? 'သိမ်းနေပါသည်...' : 'သိမ်းဆည်းမည်'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Package Price Modal */}
      {editingPackage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-xs p-4">
          <div className="bg-surface-card w-full max-w-sm rounded-3xl border border-plum-border p-5 shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between pb-2 border-b border-plum-border/60">
              <h3 className="font-bold text-plum-dark text-base">Package ပြင်ဆင်ရန်</h3>
              <span className="text-xs font-bold font-mono text-primary bg-primary/10 px-2 py-0.5 rounded-md uppercase">
                {editingPackage.operator_id}
              </span>
            </div>

            <div className="space-y-3">
              {/* Stock Status Selector */}
              <div>
                <label className="block text-xs font-semibold text-plum-dark mb-1.5">
                  စတော့ အခြေအနေ
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setEditStockActive(1)}
                    className={`py-2 px-3 rounded-xl text-xs font-bold border flex items-center justify-center space-x-1.5 transition ${
                      editStockActive === 1
                        ? 'bg-green-500/15 text-status-green border-green-500/40 shadow-xs'
                        : 'bg-surface-page text-plum-muted border-plum-border'
                    }`}
                  >
                    <span>🟢 စတော့ရှိ (ဖွင့်မည်)</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditStockActive(0)}
                    className={`py-2 px-3 rounded-xl text-xs font-bold border flex items-center justify-center space-x-1.5 transition ${
                      editStockActive === 0
                        ? 'bg-red-500/15 text-status-red border-red-500/40 shadow-xs'
                        : 'bg-surface-page text-plum-muted border-plum-border'
                    }`}
                  >
                    <span>🔴 စတော့ကုန် (ပိတ်မည်)</span>
                  </button>
                </div>
              </div>

              {/* Package Name */}
              <div>
                <label className="block text-xs font-semibold text-plum-dark mb-1">
                  Package အမည်
                </label>
                <input
                  type="text"
                  value={editPkgName}
                  onChange={(e) => setEditPkgName(e.target.value)}
                  className="w-full bg-surface-page border border-plum-border rounded-xl px-3 py-2 text-xs text-plum-dark"
                />
              </div>

              {/* Data Allowance */}
              <div>
                <label className="block text-xs font-semibold text-plum-dark mb-1">
                  ဒေတာ ပမာဏ
                </label>
                <input
                  type="text"
                  value={editPkgData}
                  onChange={(e) => setEditPkgData(e.target.value)}
                  placeholder="ဥပမာ: 1.5 GB"
                  className="w-full bg-surface-page border border-plum-border rounded-xl px-3 py-2 text-xs text-plum-dark"
                />
              </div>

              {/* Prices */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-semibold text-plum-dark mb-1">
                    ရောင်းစျေး (Ks)
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={editSellingPrice}
                    onChange={(e) => setEditSellingPrice(e.target.value)}
                    placeholder="1400"
                    className="w-full bg-surface-page border border-plum-border rounded-xl px-3 py-2 text-sm font-mono font-bold text-primary"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-plum-dark mb-1">
                    ရင်းစျေး (Ks)
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={editCostPrice}
                    onChange={(e) => setEditCostPrice(e.target.value)}
                    placeholder="1200"
                    className="w-full bg-surface-page border border-plum-border rounded-xl px-3 py-2 text-sm font-mono text-plum-dark"
                  />
                </div>
              </div>
            </div>

            <div className="flex space-x-2 pt-2">
              <button
                type="button"
                onClick={() => setEditingPackage(null)}
                className="flex-1 border border-plum-border py-2.5 rounded-xl text-xs font-semibold text-plum-muted hover:text-plum-dark"
              >
                ပယ်ဖျက်မည်
              </button>
              <button
                type="button"
                disabled={actionLoading}
                onClick={handleSavePackage}
                className="flex-1 bg-primary text-white py-2.5 rounded-xl text-xs font-semibold hover:bg-primary-hover transition"
              >
                {actionLoading ? 'သိမ်းနေပါသည်...' : 'သိမ်းဆည်းမည်'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add New Package Modal */}
      {showAddPackage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-xs p-4">
          <div className="bg-surface-card w-full max-w-sm rounded-3xl border border-plum-border p-5 shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto">
            <h3 className="font-bold text-plum-dark text-base">Package အသစ်ထည့်မည်</h3>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-plum-dark mb-1">Operator</label>
                <div className="grid grid-cols-4 gap-1.5">
                  {(['mpt', 'atom', 'u9', 'mytel'] as OperatorId[]).map((op) => (
                    <button
                      key={op}
                      type="button"
                      onClick={() => setNewPkgOp(op)}
                      className={`py-2 text-xs font-bold rounded-xl border uppercase transition ${
                        newPkgOp === op
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-plum-border bg-surface-page text-plum-muted'
                      }`}
                    >
                      {op}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-plum-dark mb-1">Package အမည်</label>
                <input
                  type="text"
                  value={newPkgName}
                  onChange={(e) => setNewPkgName(e.target.value)}
                  placeholder="ဥပမာ: MPT Data Carry 5GB"
                  className="w-full bg-surface-page border border-plum-border rounded-xl px-3 py-2 text-xs text-plum-dark"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-semibold text-plum-dark mb-1">Data Allowance</label>
                  <input
                    type="text"
                    value={newPkgData}
                    onChange={(e) => setNewPkgData(e.target.value)}
                    placeholder="ဥပမာ: 5 GB"
                    className="w-full bg-surface-page border border-plum-border rounded-xl px-3 py-2 text-xs text-plum-dark"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-plum-dark mb-1">သက်တမ်း (ရက်)</label>
                  <input
                    type="number"
                    value={newPkgValidity}
                    onChange={(e) => setNewPkgValidity(e.target.value)}
                    placeholder="30"
                    className="w-full bg-surface-page border border-plum-border rounded-xl px-3 py-2 text-xs text-plum-dark font-mono"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-semibold text-plum-dark mb-1">ရောင်းစျေး (Ks)</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={newPkgSellingPrice}
                    onChange={(e) => setNewPkgSellingPrice(e.target.value)}
                    placeholder="5200"
                    className="w-full bg-surface-page border border-plum-border rounded-xl px-3 py-2 text-xs text-plum-dark font-mono font-bold text-primary"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-plum-dark mb-1">ရင်းစျေး (Ks)</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={newPkgCostPrice}
                    onChange={(e) => setNewPkgCostPrice(e.target.value)}
                    placeholder="4500"
                    className="w-full bg-surface-page border border-plum-border rounded-xl px-3 py-2 text-xs text-plum-dark font-mono"
                  />
                </div>
              </div>
            </div>

            <div className="flex space-x-2 pt-2">
              <button
                type="button"
                onClick={() => setShowAddPackage(false)}
                className="flex-1 border border-plum-border py-2.5 rounded-xl text-xs font-semibold text-plum-muted hover:text-plum-dark"
              >
                ပယ်ဖျက်မည်
              </button>
              <button
                type="button"
                disabled={actionLoading}
                onClick={handleCreateNewPackage}
                className="flex-1 bg-primary text-white py-2.5 rounded-xl text-xs font-semibold hover:bg-primary-hover transition"
              >
                {actionLoading ? 'ထည့်နေပါသည်...' : 'အသစ်ထည့်မည်'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add / Edit Airtime Denomination Modal */}
      {showAddDenomination && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-xs p-4">
          <div className="bg-surface-card w-full max-w-sm rounded-3xl border border-plum-border p-5 shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto">
            <h3 className="font-bold text-plum-dark text-base">
              {editingDenomination ? 'ဘေလ်ပမာဏ ပြင်ဆင်ရန်' : 'ဘေလ်ပမာဏ အသစ်ထည့်မည်'}
            </h3>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-plum-dark mb-1">Operator</label>
                <div className="grid grid-cols-4 gap-1.5">
                  {(['mpt', 'atom', 'u9', 'mytel'] as OperatorId[]).map((op) => (
                    <button
                      key={op}
                      type="button"
                      onClick={() => setDenomOp(op)}
                      className={`py-2 text-xs font-bold rounded-xl border uppercase transition ${
                        denomOp === op
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-plum-border bg-surface-page text-plum-muted'
                      }`}
                    >
                      {op}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-plum-dark mb-1">
                  ဘေလ်ပမာဏ (Ks Airtime)
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={denomAmount}
                  onChange={(e) => setDenomAmount(e.target.value)}
                  placeholder="ဥပမာ: 1000, 2000, 3000, 5000"
                  className="w-full bg-surface-page border border-plum-border rounded-xl px-3 py-2 text-sm font-mono font-bold text-plum-dark"
                />
                <p className="text-[10px] text-plum-muted mt-1">
                  Customer ထံသို့ လွှဲပေးရမည့် ဖုန်းဘေလ် ပမာဏ
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-semibold text-plum-dark mb-1">
                    ရောင်းစျေး (Ks)
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={denomSellingPrice}
                    onChange={(e) => setDenomSellingPrice(e.target.value)}
                    placeholder="ဥပမာ: 950"
                    className="w-full bg-surface-page border border-plum-border rounded-xl px-3 py-2 text-sm font-mono font-bold text-primary"
                  />
                  <p className="text-[10px] text-plum-muted mt-1">
                    Customer ပေးချေရမည့် ငွေ
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-plum-dark mb-1">
                    ရင်းစျေး (Ks)
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={denomCostPrice}
                    onChange={(e) => setDenomCostPrice(e.target.value)}
                    placeholder="ဥပမာ: 900"
                    className="w-full bg-surface-page border border-plum-border rounded-xl px-3 py-2 text-sm font-mono text-plum-dark"
                  />
                  <p className="text-[10px] text-plum-muted mt-1">
                    အမြတ်တွက်ရန် အရင်း
                  </p>
                </div>
              </div>
            </div>

            <div className="flex space-x-2 pt-2">
              <button
                type="button"
                onClick={() => {
                  setShowAddDenomination(false);
                  setEditingDenomination(null);
                }}
                className="flex-1 border border-plum-border py-2.5 rounded-xl text-xs font-semibold text-plum-muted hover:text-plum-dark"
              >
                ပယ်ဖျက်မည်
              </button>
              <button
                type="button"
                disabled={actionLoading}
                onClick={handleSaveDenomination}
                className="flex-1 bg-primary text-white py-2.5 rounded-xl text-xs font-semibold hover:bg-primary-hover transition"
              >
                {actionLoading ? 'သိမ်းနေပါသည်...' : 'သိမ်းဆည်းမည်'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
